
import { mergeMapTo, filter, mergeMap, first, delay, mapTo, catchError, map } from 'rxjs/operators'
import { combineLatest, from, empty, of } from 'rxjs'
import { combineEpics } from 'redux-observable'
import { activateAnchorActionCreator, pushActionCreator } from 'actions/navigationActions'
import { processError } from 'helpers/ajaxHelpers'
import { normalize } from 'helpers/normalizeHelpers'
import {
  FORM_DEVICE_VERIFICATION,
  FORM_LOGIN,
  FORM_ONBOARDING_REGISTER,
  FORM_PASSWORD_CHANGE,
  FORM_PASSWORD_CODE_SEND,
  FORM_PASSWORD_RESET,
} from 'constants/forms'
import { showPasswordChangeFormActionCreator } from 'actions/settingsActions'
import { GET_MEMBER_AUTH_STATUS, SET_MEMBER_INFO, getMemberAuthStatusActionCreator, setMemberInfoActionCreator } from 'actions/memberActions'
import {
  DELETE_DEVICE,
  failDeleteDeviceActionCreator,
  GET_DEVICES,
  GET_PASSWORD_STRENGTH,
  getPasswordStrengthActionCreator,
  loggedInActionCreator,
  loggedOutActionCreator,
  LOGOUT,
  logOutActionCreator,
  setDevicesActionCreator,
  setDeviceUnverifiedActionCreator,
  setDeviceVerifiedActionCreator,
  togglePasswordResetCodeSentActionCreator,
} from 'actions/authActions'
import { PAGE_CHANGED, resetStateActionCreator } from 'actions/globalActions'
import { Epic } from 'interfaces/middleware'
import { CHECK_PASSWORD_URL, DELETE_DEVICE_URL, GET_DEVICES_URL, LOGOUT_MEMBER_URL, PING_URL } from 'interfaces/ajax'
import { LOGIN_URL, ONBOARDING_REGISTER_URL, PASSWORD_RESET_URL, INTERNAL_ENTRY_URL } from 'tangle-shared'
import { getAnchorByUrl, isInternalUrl } from 'helpers/pageHelpers'
import { Action } from 'actions'
import { Member } from 'interfaces/member'
import { destroy, stopSubmit } from 'redux-form'
import { isAuthenticatedSelector, memberInfoSelector } from 'selectors/globalSelectors'
import { ONBOARDING_STEP_URL_BY_NAME } from 'constants/onboarding'
import { getCurrentUrl } from 'helpers/utilsHelpers'
import { UNAUTHORIZED_STATUS_CODE } from 'constants/ajax'
import { isAjaxError } from 'helpers/errorHelpers'
import { get, post } from 'helpers/observableAjaxHelpers'
import { RETURN_URL_PARAM_NAME } from 'constants/pages'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { formSubmitted, formSubmitFailed } from 'rxOperators/formsLegacy'
import { mapSafe, mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { catchSafe } from 'rxOperators/catchSafe'
import { FORM_SETTINGS_CREDIT, FORM_SETTINGS_CREDIT_EXTENDED } from 'constants/forms'
import { fetchCTAFromURLActionCreator } from 'actions/insightsActions'

// ===========================================================================//
//                                LOGIN / LOGOUT                              //
// ===========================================================================//

// should logout when move on /onboarding/register page
const registerLogoutEpic: Epic = (action$, store) => {
  const registerOpened$ = action$
    .pipe(byType(PAGE_CHANGED)).pipe(
      filter((a) => a.payload.next === ONBOARDING_REGISTER_URL))

  const setMemberInfo$ = action$
    .pipe(byType(SET_MEMBER_INFO))

  return combineLatest(registerOpened$, setMemberInfo$).pipe(
    mergeMap(() => {
      const state = store.getState()
      const isAuthenticated = isAuthenticatedSelector(state)
      const url = getCurrentUrl()

      if (isAuthenticated && url === ONBOARDING_REGISTER_URL) {
        const postResetActions = [
          logOutActionCreator({ redirect: false }),
        ]

        return of(
          resetStateActionCreator(() => postResetActions),
        )
      }

      return empty()
    }))
}

const loggedInEpic: Epic = (action$) => action$.pipe(
  formSubmitted(FORM_LOGIN, FORM_DEVICE_VERIFICATION, FORM_ONBOARDING_REGISTER),
  mergeMap(({ payload }) => {
    if (
      payload.formName !== FORM_LOGIN
      && payload.formName !== FORM_DEVICE_VERIFICATION
      && payload.formName !== FORM_ONBOARDING_REGISTER
    ) {
      return empty()
    }

    const isLogin = payload.formName === FORM_LOGIN || payload.formName === FORM_DEVICE_VERIFICATION
    const member: Member = payload.formName === FORM_LOGIN || payload.formName === FORM_DEVICE_VERIFICATION
      ? payload.response.member as Member
      : payload.response

    const successActions = [
      setMemberInfoActionCreator(member),
      setDeviceVerifiedActionCreator(),
      loggedInActionCreator(isLogin ? 'login' : 'register'),
      fetchCTAFromURLActionCreator(),
    ]

    const isCustomRegisterFlow = payload.formName === FORM_ONBOARDING_REGISTER && (
      false
      || payload.formValues.form_mode === 'meeting-panel'
      || payload.formValues.form_mode === 'credit-card'
    )
    if (isCustomRegisterFlow) {
      return of(...successActions)
    }

    if (payload.formName === FORM_LOGIN && payload.response.status === 'unverified_device') {
      return of(setDeviceUnverifiedActionCreator())
    }

    const currentRoute = getCurrentUrl()
    if (
      currentRoute !== LOGIN_URL
      && (
        payload.formName === FORM_LOGIN && payload.response.status === 'ok'
        || payload.formName === FORM_DEVICE_VERIFICATION
      )
    ) {
      // Prevent scenario where someone submits login info but then navigates away from the page during the request
      // They can end up on onboarding with their member info filled in.
      return empty()
    }

    const { searchParams } = new URL(document.location.href)
    const returnUrl = searchParams.get(RETURN_URL_PARAM_NAME)
    const redirectUrl = (() => {
      if (payload.formName !== FORM_ONBOARDING_REGISTER) {
        return returnUrl
          ? decodeURIComponent(returnUrl)
          : ONBOARDING_STEP_URL_BY_NAME[member.onboarding_step]
      }

      if (returnUrl) {
        return `${ ONBOARDING_STEP_URL_BY_NAME[member.onboarding_step] }?${ RETURN_URL_PARAM_NAME }=${ encodeURIComponent(returnUrl) }`
      }

      return ONBOARDING_STEP_URL_BY_NAME[member.onboarding_step]
    })()

    const anchor = redirectUrl === INTERNAL_ENTRY_URL ? 'dashboard' as const :
      getAnchorByUrl(redirectUrl)

    return of(resetStateActionCreator(() => {
      const nextActions = [
        pushActionCreator(redirectUrl),
        ...successActions,
        stopSubmit(payload.formName),
      ]

      if (anchor) {
        nextActions.push(activateAnchorActionCreator(anchor))
      }
      return nextActions
    }))
  })
)

const failSubmitLoginEpic: Epic = (action$, store) =>
  action$
    .pipe(formSubmitFailed(FORM_LOGIN))
    .pipe(mergeMapSafe(() => {
      const isAuthenticated = isAuthenticatedSelector(store.getState())
      if (isAuthenticated) {
        return of(
          resetStateActionCreator(() => [
            loggedOutActionCreator(),
          ]),
        )
      }

      return empty()
    }))

const logoutEpic: Epic = (action$) =>
  action$
    .pipe(byType(LOGOUT)).pipe(
      mergeMap(({ payload }) =>
        post(LOGOUT_MEMBER_URL)
          .pipe(mergeMapSafe(() => {
            const actions: Action[] = [
              resetStateActionCreator(() => [
                loggedOutActionCreator(),
                destroy(FORM_SETTINGS_CREDIT, FORM_SETTINGS_CREDIT_EXTENDED),
              ]),
            ]

            // if redirect to payload url - it's redirected by internal page guard
            if (payload.redirect && payload.to && payload.to !== LOGIN_URL) {
              actions.unshift(
                pushActionCreator(payload.to),
              )
            }

            return of(...actions)
          })),
      ))

const checkExternalAuth: Epic = (action$, store) =>
  action$.pipe(
    first(),
    // wait for probable post-reset actions
    delay(100),
    filter(() => {
      const url = getCurrentUrl()
      return !isInternalUrl(url)
    }),
    filter(() => {
      const memberInfo = memberInfoSelector(store.getState())
      return !memberInfo
    }))
    .pipe(mapSafe(getMemberAuthStatusActionCreator))

const getMemberAuthStatus: Epic = (action$) =>
  action$
    .pipe(byType(GET_MEMBER_AUTH_STATUS)).pipe(
      mergeMap(() =>
        post(PING_URL).pipe(
          mapTo(
            loggedInActionCreator('session-continue'),
          ),
          catchError((error) => {
            if (isAjaxError(error)) {
              const { status } = error
              if (status === UNAUTHORIZED_STATUS_CODE) {
                return of(loggedOutActionCreator())
              }
            }

            return processError(error, true)
          })),
      ))

// `LoginForm` should be cleared manually because it has `destroyOnUnmount: false`.
const clearLoginFormEpic: Epic = (action$) =>
  action$
    .pipe(byType(PAGE_CHANGED)).pipe(
      mergeMap((a) => {
        const { prev, next } = a.payload

        if (prev === LOGIN_URL && next === PASSWORD_RESET_URL) {
          return empty()
        }

        return of(destroy(FORM_LOGIN))
      }))


// ===========================================================================//
//                                   PASSWORDS                                //
// ===========================================================================//

const validatePasswordEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_PASSWORD_STRENGTH, 'request')).pipe(
      mergeMap(({ payload }) => {
        const { success, fail } = getPasswordStrengthActionCreator

        return post(CHECK_PASSWORD_URL, {
          body: {
            password: payload.params,
          },
        }).pipe(
          mergeMap(({ response }) => of(success(payload.params, response))))
          .pipe(catchSafe(() =>
            of(
              fail(payload.params)
            )
          ))
      }))

const passwordCodeEpic: Epic = (action$) =>
  action$
    .pipe(formSubmitted(FORM_PASSWORD_CODE_SEND)).pipe(
      mergeMap(() => of(togglePasswordResetCodeSentActionCreator(true))))

const passwordResetEpic: Epic = (action$) =>
  action$
    .pipe(formSubmitted(FORM_PASSWORD_RESET)).pipe(
      mergeMap(() => from([
        loggedInActionCreator('login'),
        pushActionCreator(INTERNAL_ENTRY_URL),
        togglePasswordResetCodeSentActionCreator(false),
      ])))

const passwordChangeEpic: Epic = (action$) =>
  action$
    .pipe(formSubmitted(FORM_PASSWORD_CHANGE)).pipe(
      map(() => showPasswordChangeFormActionCreator(false)))

// ===========================================================================//
//                                    DEVICES                                 //
// ===========================================================================//

const getDevicesEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_DEVICES)).pipe(
      mergeMap(() =>
        get(GET_DEVICES_URL)
          .pipe(mapSafe(({ response }) =>
            setDevicesActionCreator(
              normalize(response, 'id'),
            ),
          )),
      ))

const deleteDeviceEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(DELETE_DEVICE)).pipe(
      mergeMap((action) => {
        const id = action.payload
        const devices = store.getState().data.auth.devices
        if (!devices) {
          throw new Error(`DELETE_DEVICE can't be called before you have devices`)
        }
        const device = devices.byKey[id]
        const requestParams = {
          url: DELETE_DEVICE_URL,
          params: {
            id: String(id),
          },
        }

        return post(requestParams).pipe(
          mergeMapTo(empty()))
          .pipe(catchSafe(() =>
            of(
              failDeleteDeviceActionCreator(device),
            ),
          ))
      }))

export default combineEpics(
  getDevicesEpic,
  deleteDeviceEpic,
  passwordChangeEpic,
  passwordCodeEpic,
  passwordResetEpic,
  loggedInEpic,
  failSubmitLoginEpic,
  clearLoginFormEpic,
  logoutEpic,
  registerLogoutEpic,
  checkExternalAuth,
  getMemberAuthStatus,
  validatePasswordEpic,
)
